import { useCallback, useEffect, useRef, useState } from 'react'
import { apiGet } from '../../../../../generic/Api_Functions'
import { jobDetailBlockUrl, jobDetailPartUrl, jobDetailUrl } from '../utils/jobDetailApi'
import { JOB_DETAIL_TAB, isBlockTab, nextActiveTab, visibleTabs } from '../utils/jobDetailTabs'
import { keepOrFirstPart, keepOrInitialIncidence } from '../utils/jobDetailFormat'

// Historia 160 — el estado del detalle del job (03-dominio.md §7) y TODAS sus llamadas (§8.5).
//
// ⚠️ UN SOLO RELOJ, PROPIO, DE 5 s (D7/RN44). No hereda el sondeo de 1 s de la tabla que lo abrió.
// Cada ciclo (apertura, cambio de job, cambio de pestaña, tick, recarga tras una acción) programa el
// SIGUIENTE al terminar, así que dos refrescos nunca se solapan (S124) y una recarga en el acto
// vuelve a poner el reloj a contar desde ella (S66).
//
// ⚠️ GENERACIÓN (RN45). Cada ciclo nuevo sube `generationRef`; una respuesta que vuelve con una
// generación vieja —un job que ya no es el seleccionado, una pestaña que ya no se ve, un detalle ya
// cerrado— se descarta sin tocar nada (S17, S21, S127).
//
// ⚠️ SIN PARPADEO (T14/S123). Todo setter compara con lo mostrado y, si es igual, devuelve el MISMO
// objeto: React no repinta. Los refrescos nunca ponen indicador de carga.
//
// ⚠️ Documentos no está aquí: la pestaña monta el panel de #148, que carga solo al cambiar el par
// (RN36). Por eso un tick con Documentos visible pide solo D1 (S116/S122).

export const JOB_DETAIL_POLL_MS = 5000

export const FIRST_LOAD = { LOADING: 'LOADING', READY: 'READY', FAILED: 'FAILED' }
export const BLOCK_STATUS = { IDLE: 'IDLE', LOADING: 'LOADING', READY: 'READY', FAILED: 'FAILED' }

const EMPTY_BLOCK = { status: BLOCK_STATUS.IDLE, data: {}, jobReferenceId: '' }
const EMPTY_BLOCKS = {
  [JOB_DETAIL_TAB.MATERIALS]: EMPTY_BLOCK,
  [JOB_DETAIL_TAB.INSPECTIONS]: EMPTY_BLOCK,
  [JOB_DETAIL_TAB.INCIDENCES]: EMPTY_BLOCK,
  [JOB_DETAIL_TAB.PARTS]: EMPTY_BLOCK
}
const EMPTY_FOOTPRINT = { status: BLOCK_STATUS.IDLE, data: {}, partId: '', errorKey: '' }

// Comparación estructural de respuestas JSON de Sun: mismo contenido ⇒ se conserva el objeto viejo.
const sameContent = (left, right) => JSON.stringify(left) === JSON.stringify(right)

const errorLiteralOf = (error) => (error && typeof error.message === 'string' ? error.message : '')

/**
 * @param {Object} params
 * @param {boolean} params.open
 * @param {string|number} params.fabricationOrderId
 * @param {string|number} params.initialJob `jobReferenceId` de la fila pulsada
 * @param {string|number} params.deviceId máquina del puesto (RN20)
 * @param {boolean} params.canViewDocuments D16
 * @param {Function} params.onRefreshError (literal) => void — fallo de un refresco (E5/RN53)
 * @param {Function} params.onNavigationError (literal) => void — fallo al cambiar de job (CU2 A2) o RN7
 */
const useJobDetail = ({
  open = false,
  fabricationOrderId = '',
  initialJob = '',
  deviceId = '',
  canViewDocuments = false,
  onRefreshError = () => {},
  onNavigationError = () => {}
}) => {
  const [firstLoad, setFirstLoad] = useState(FIRST_LOAD.LOADING)
  const [firstError, setFirstError] = useState('')
  const [detail, setDetail] = useState({})
  const [selectedJobId, setSelectedJobId] = useState('')
  const [switching, setSwitching] = useState(false)
  const [activeTab, setActiveTab] = useState(JOB_DETAIL_TAB.GENERAL)
  const [blocks, setBlocks] = useState(EMPTY_BLOCKS)
  const [selectedIncidenceUuid, setSelectedIncidenceUuid] = useState('')
  const [selectedPartId, setSelectedPartId] = useState('')
  const [footprint, setFootprint] = useState(EMPTY_FOOTPRINT)

  // Lo vigente, legible desde los ciclos asíncronos sin cerrar sobre un render viejo.
  const generationRef = useRef(0)
  const timerRef = useRef(0)
  const openRef = useRef(false)
  const jobRef = useRef('')
  const tabRef = useRef(JOB_DETAIL_TAB.GENERAL)
  const partRef = useRef('')
  const incidenceRef = useRef('')
  const footprintTokenRef = useRef(0)
  const refreshRef = useRef(() => {})
  const paramsRef = useRef({})
  paramsRef.current = { fabricationOrderId, initialJob, deviceId, canViewDocuments, onRefreshError, onNavigationError }

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = 0
    }
  }

  // T14: se compara con lo MOSTRADO antes de pedir un render. Ni siquiera se encola una
  // actualización si el contenido es el mismo: un `setState` funcional que devolviera el mismo objeto
  // todavía obligaría a React a repintar este componente para descubrirlo (S123).
  const shownRef = useRef({ detail: {}, blocks: EMPTY_BLOCKS, footprint: EMPTY_FOOTPRINT })

  const applyDetail = (data) => {
    if (sameContent(shownRef.current.detail, data)) return
    shownRef.current.detail = data
    setDetail(data)
  }

  const applyBlock = (tab, block) => {
    if (sameContent(shownRef.current.blocks[tab], block)) return
    shownRef.current.blocks = { ...shownRef.current.blocks, [tab]: block }
    setBlocks(shownRef.current.blocks)
  }

  const applyFootprint = (next) => {
    if (sameContent(shownRef.current.footprint, next)) return
    shownRef.current.footprint = next
    setFootprint(next)
  }

  const selectTabInternal = (tab) => {
    tabRef.current = tab
    setActiveTab(tab)
  }

  // Selecciones: se actualizan solo si cambian (un refresco que conserva la elegida no repinta).
  const selectIncidenceInternal = (uuid) => {
    if (uuid === incidenceRef.current) return
    incidenceRef.current = uuid
    setSelectedIncidenceUuid(uuid)
  }

  const selectPartInternal = (partId) => {
    if (partId === partRef.current) return
    partRef.current = partId
    setSelectedPartId(partId)
  }

  // D6 de la pieza vigente. Su propio token: pulsar otra pieza descarta la huella anterior.
  const loadFootprint = useCallback(async (jobReferenceId, partId, silent) => {
    footprintTokenRef.current += 1
    const token = footprintTokenRef.current
    const { fabricationOrderId: foId } = paramsRef.current
    if (partId === '') {
      applyFootprint(EMPTY_FOOTPRINT)
      return
    }
    if (!silent) {
      const shown = shownRef.current.footprint
      if (!(shown.partId === partId && shown.status === BLOCK_STATUS.READY)) {
        applyFootprint({ ...EMPTY_FOOTPRINT, status: BLOCK_STATUS.LOADING, partId })
      }
    }
    try {
      const data = await apiGet(jobDetailPartUrl(foId, jobReferenceId, partId))
      if (token !== footprintTokenRef.current || jobRef.current !== jobReferenceId || !openRef.current) return
      applyFootprint({ status: BLOCK_STATUS.READY, data: data || {}, partId, errorKey: '' })
    } catch (error) {
      if (token !== footprintTokenRef.current || jobRef.current !== jobReferenceId || !openRef.current) return
      if (silent && error?.status !== 404) {
        paramsRef.current.onRefreshError(errorLiteralOf(error))
        return
      }
      // E3/G10: 404 `PartNotFoundInFabricationOrder` va DENTRO del panel de la huella (S99).
      applyFootprint({ status: BLOCK_STATUS.FAILED, data: {}, partId, errorKey: errorLiteralOf(error) || 'This block could not be loaded' })
    }
  }, [])

  // D2-D5 (y, en Piezas, D6 de la pieza que quede seleccionada). Devuelve false si se descartó.
  const loadBlock = useCallback(async (tab, jobReferenceId, generation, silent) => {
    const { fabricationOrderId: foId } = paramsRef.current
    const isCurrent = () => generation === generationRef.current && openRef.current
    if (!silent) {
      const current = shownRef.current.blocks[tab]
      // Datos de este mismo job ya pintados: se mantienen mientras llega la respuesta.
      if (!(current.jobReferenceId === jobReferenceId && current.status === BLOCK_STATUS.READY)) {
        applyBlock(tab, { status: BLOCK_STATUS.LOADING, data: current.data, jobReferenceId: current.jobReferenceId })
      }
    }
    let data
    try {
      data = await apiGet(jobDetailBlockUrl(tab, foId, jobReferenceId))
    } catch (error) {
      if (!isCurrent()) return false
      if (silent) {
        paramsRef.current.onRefreshError(errorLiteralOf(error))
        return true
      }
      // E4/D12: el fallo se pinta DENTRO de la pestaña, con Reintentar; el resto sigue (S44).
      applyBlock(tab, { status: BLOCK_STATUS.FAILED, data: {}, jobReferenceId })
      return true
    }
    if (!isCurrent()) return false
    const payload = data || {}
    applyBlock(tab, { status: BLOCK_STATUS.READY, data: payload, jobReferenceId })
    if (tab === JOB_DETAIL_TAB.INCIDENCES) {
      selectIncidenceInternal(keepOrInitialIncidence(incidenceRef.current, payload.incidences || []))
    }
    if (tab === JOB_DETAIL_TAB.PARTS) {
      selectPartInternal(keepOrFirstPart(partRef.current, payload.parts || []))
      await loadFootprint(jobReferenceId, partRef.current, silent)
    }
    return true
  }, [loadFootprint])

  // El reloj: el siguiente tick se programa SIEMPRE al terminar el ciclo anterior.
  const scheduleTick = useCallback((generation) => {
    clearTimer()
    if (generation !== generationRef.current || !openRef.current) return
    timerRef.current = setTimeout(() => {
      timerRef.current = 0
      refreshRef.current()
    }, JOB_DETAIL_POLL_MS)
  }, [])

  const selectJobInternal = useCallback(async (jobReferenceId, { revertTo = '' } = {}) => {
    clearTimer()
    generationRef.current += 1
    const generation = generationRef.current
    const previousJob = revertTo !== '' ? revertTo : jobRef.current
    const { fabricationOrderId: foId, deviceId: device, canViewDocuments: documents } = paramsRef.current
    jobRef.current = jobReferenceId
    setSelectedJobId(jobReferenceId)
    // CU2 paso 2: la pieza y la incidencia elegidas eran del job anterior.
    selectPartInternal('')
    selectIncidenceInternal('')
    applyFootprint(EMPTY_FOOTPRINT)
    setSwitching(true)
    let data
    try {
      data = await apiGet(jobDetailUrl(foId, jobReferenceId, device))
    } catch (error) {
      if (generation !== generationRef.current || !openRef.current) return
      // CU2 A2: no se cierra; se vuelve a la tarjeta anterior con sus datos.
      jobRef.current = previousJob
      setSelectedJobId(previousJob)
      setSwitching(false)
      paramsRef.current.onNavigationError(errorLiteralOf(error))
      scheduleTick(generation)
      return
    }
    if (generation !== generationRef.current || !openRef.current) return
    applyDetail(data || {})
    const tab = nextActiveTab(tabRef.current, visibleTabs(data?.summary, documents))
    selectTabInternal(tab)
    if (isBlockTab(tab)) {
      await loadBlock(tab, jobReferenceId, generation, false)
      if (generation !== generationRef.current) return
    }
    setSwitching(false)
    scheduleTick(generation)
  }, [loadBlock, scheduleTick])

  // Tick de refresco y recarga en el acto tras una acción propia: D1 y la pestaña visible.
  const runRefresh = useCallback(async () => {
    clearTimer()
    if (!openRef.current) return
    generationRef.current += 1
    const generation = generationRef.current
    const jobReferenceId = jobRef.current
    const { fabricationOrderId: foId, deviceId: device, canViewDocuments: documents, initialJob: rowJob } = paramsRef.current
    let data
    try {
      data = await apiGet(jobDetailUrl(foId, jobReferenceId, device))
    } catch (error) {
      if (generation !== generationRef.current || !openRef.current) return
      // E5/POL3: se conservan los datos, aviso no bloqueante y el siguiente tick reintenta (S125).
      paramsRef.current.onRefreshError(errorLiteralOf(error))
      scheduleTick(generation)
      return
    }
    if (generation !== generationRef.current || !openRef.current) return
    const sequence = data?.sequence || []
    if (!sequence.some((item) => item.jobReferenceId === jobReferenceId)) {
      // RN7: el job seleccionado ya no está en la secuencia ⇒ el de la fila; si tampoco, error E2 y
      // no se elige otro por iniciativa propia (S20).
      if (jobReferenceId !== rowJob && sequence.some((item) => item.jobReferenceId === rowJob)) {
        selectJobInternal(rowJob, { revertTo: jobReferenceId })
        return
      }
      paramsRef.current.onNavigationError('JobNotFoundInFabricationOrder')
      scheduleTick(generation)
      return
    }
    applyDetail(data || {})
    const visible = visibleTabs(data?.summary, documents)
    const tab = nextActiveTab(tabRef.current, visible)
    if (tab !== tabRef.current) {
      // RN26/S35: la pestaña activa desaparece ⇒ General, sin pedir su bloque.
      selectTabInternal(tab)
    } else if (isBlockTab(tab)) {
      const kept = await loadBlock(tab, jobReferenceId, generation, true)
      if (!kept) return
    }
    scheduleTick(generation)
  }, [loadBlock, scheduleTick, selectJobInternal])
  refreshRef.current = runRefresh

  // Apertura: una D1 (S1). Si falla, diálogo de error y NINGÚN reloj (S19).
  useEffect(() => {
    if (!open) {
      openRef.current = false
      generationRef.current += 1
      clearTimer()
      return
    }
    openRef.current = true
    clearTimer()
    generationRef.current += 1
    const generation = generationRef.current
    const { fabricationOrderId: foId, deviceId: device } = paramsRef.current
    jobRef.current = initialJob
    tabRef.current = JOB_DETAIL_TAB.GENERAL
    partRef.current = ''
    incidenceRef.current = ''
    setSelectedJobId(initialJob)
    setActiveTab(JOB_DETAIL_TAB.GENERAL)
    setFirstLoad(FIRST_LOAD.LOADING)
    setFirstError('')
    shownRef.current = { detail: {}, blocks: EMPTY_BLOCKS, footprint: EMPTY_FOOTPRINT }
    setDetail({})
    setBlocks(EMPTY_BLOCKS)
    setSelectedIncidenceUuid('')
    setSelectedPartId('')
    setFootprint(EMPTY_FOOTPRINT)
    setSwitching(false)
    apiGet(jobDetailUrl(foId, initialJob, device))
      .then((data) => {
        if (generation !== generationRef.current || !openRef.current) return
        applyDetail(data || {})
        setFirstLoad(FIRST_LOAD.READY)
        scheduleTick(generation)
      })
      .catch((error) => {
        if (generation !== generationRef.current || !openRef.current) return
        setFirstError(errorLiteralOf(error))
        setFirstLoad(FIRST_LOAD.FAILED)
      })
  }, [open, initialJob, fabricationOrderId, scheduleTick])

  // Al desmontar, nada queda programado ni vivo (S21).
  useEffect(() => () => {
    openRef.current = false
    generationRef.current += 1
    clearTimer()
  }, [])

  const selectJob = useCallback((jobReferenceId) => {
    if (jobReferenceId === jobRef.current) return
    selectJobInternal(jobReferenceId)
  }, [selectJobInternal])

  const selectTab = useCallback(async (tab) => {
    if (tab === tabRef.current) return
    clearTimer()
    generationRef.current += 1
    const generation = generationRef.current
    selectTabInternal(tab)
    if (tab === JOB_DETAIL_TAB.PARTS) {
      // RN39/D24: al entrar, la primera pieza de la rejilla.
      selectPartInternal('')
    }
    if (isBlockTab(tab)) {
      const kept = await loadBlock(tab, jobRef.current, generation, false)
      if (!kept) return
    }
    scheduleTick(generation)
  }, [loadBlock, scheduleTick])

  const retryBlock = useCallback(async () => {
    const tab = tabRef.current
    if (!isBlockTab(tab)) return
    clearTimer()
    generationRef.current += 1
    const generation = generationRef.current
    const kept = await loadBlock(tab, jobRef.current, generation, false)
    if (kept) scheduleTick(generation)
  }, [loadBlock, scheduleTick])

  // Seleccionar otra incidencia: sus acciones ya viajan en D4, no se llama al servidor (S77).
  const selectIncidence = useCallback((uuid) => selectIncidenceInternal(uuid), []) // eslint-disable-line react-hooks/exhaustive-deps

  const selectPart = useCallback((partId) => {
    if (partId === partRef.current) return
    selectPartInternal(partId)
    loadFootprint(jobRef.current, partId, false)
  }, [loadFootprint]) // eslint-disable-line react-hooks/exhaustive-deps

  // RN23: tras Interrupt/Finish, recarga en el acto (D1 + pestaña visible) y el reloj vuelve a contar.
  const reloadNow = useCallback(() => runRefresh(), [runRefresh])

  return {
    firstLoad,
    firstError,
    detail,
    selectedJobId,
    switching,
    activeTab,
    blocks,
    selectedIncidenceUuid,
    selectedPartId,
    footprint,
    selectJob,
    selectTab,
    retryBlock,
    selectIncidence,
    selectPart,
    reloadNow
  }
}

export default useJobDetail
